import { useEffect, useMemo, useRef, useState } from 'react'
import { notifications } from '@mantine/notifications'
import { api } from '../../lib/api'
import {
  useEmpresaList,
  useEmpresaUpdate,
  type Empresa,
  type PapelEmpresa,
} from '../../hooks/useEmpresas'

const PAPEIS_OPTIONS: { value: PapelEmpresa; label: string; tooltip: string }[] = [
  {
    value: 'CLIENTE',
    label: 'Cliente (Contratante)',
    tooltip: 'Marque se sua empresa atua como Cliente em PMOCs.',
  },
  {
    value: 'PLANEJADOR',
    label: 'Planejador',
    tooltip: 'Marque se sua empresa atua como Planejador (RT projeta o PMOC).',
  },
  {
    value: 'EXECUTOR',
    label: 'Executor',
    tooltip:
      'Marque se sua empresa atua como Executor (manutenção mecânica) em PMOCs.',
  },
  {
    value: 'LABORATORIO',
    label: 'Laboratório',
    tooltip:
      'Marque se sua empresa atua como Laboratório (análise QAI) em PMOCs.',
  },
]

const MAX_LOGO_BYTES = 2 * 1024 * 1024
const TIPOS_ACEITOS = ['image/jpeg', 'image/png', 'image/svg+xml']

function corDeNome(nome: string): string {
  // Mesma ideia de avatar GitHub: cor estável derivada do nome.
  const cores = ['#1565C0', '#00897B', '#F57C00', '#7B1FA2', '#C2185B', '#388E3C']
  let hash = 0
  for (const ch of nome) hash = (hash * 31 + ch.charCodeAt(0)) & 0xffffffff
  return cores[Math.abs(hash) % cores.length]
}

function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean)
  if (partes.length === 0) return '?'
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase()
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase()
}

interface Props {
  isAdmin: boolean
}

export default function MinhaEmpresaSection({ isAdmin }: Props) {
  const empresaSelfQuery = useEmpresaList({ is_self: true, page_size: 1 })
  const empresa: Empresa | undefined = empresaSelfQuery.data?.results[0]

  const [papeisLocal, setPapeisLocal] = useState<PapelEmpresa[]>([])
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoErro, setLogoErro] = useState<string>('')
  const [salvando, setSalvando] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (empresa) setPapeisLocal(empresa.papeis)
  }, [empresa])

  const update = useEmpresaUpdate(empresa?.id ?? '')

  const podeSalvarPapeis = useMemo(() => {
    if (!empresa || !isAdmin) return false
    if (papeisLocal.length === 0) return false
    const igual =
      papeisLocal.length === empresa.papeis.length &&
      papeisLocal.every((p) => empresa.papeis.includes(p))
    return !igual
  }, [empresa, papeisLocal, isAdmin])

  function togglePapel(p: PapelEmpresa) {
    setPapeisLocal((cur) => {
      const set = new Set(cur)
      if (set.has(p)) set.delete(p)
      else set.add(p)
      return Array.from(set)
    })
  }

  async function salvarPapeis() {
    if (!empresa) return
    if (papeisLocal.length === 0) {
      notifications.show({
        color: 'red',
        message: 'Selecione pelo menos um papel.',
      })
      return
    }
    try {
      await update.mutateAsync({ papeis: papeisLocal })
      notifications.show({ color: 'green', message: 'Papéis atualizados.' })
    } catch {
      notifications.show({
        color: 'red',
        message: 'Não foi possível atualizar os papéis.',
      })
    }
  }

  function selecionarLogo(file: File | null) {
    setLogoErro('')
    if (!file) {
      setLogoFile(null)
      return
    }
    if (!TIPOS_ACEITOS.includes(file.type)) {
      setLogoErro('Formato inválido — use JPG, PNG ou SVG.')
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoErro('Arquivo muito grande — máximo 2MB.')
      return
    }
    setLogoFile(file)
  }

  async function uploadLogo() {
    if (!empresa || !logoFile) return
    setSalvando(true)
    try {
      const fd = new FormData()
      fd.append('logo', logoFile)
      await api.patch(`/empresas/${empresa.id}/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      notifications.show({ color: 'green', message: 'Logo enviada.' })
      setLogoFile(null)
      if (fileRef.current) fileRef.current.value = ''
      empresaSelfQuery.refetch()
    } catch {
      notifications.show({
        color: 'red',
        message: 'Não foi possível enviar a logo.',
      })
    } finally {
      setSalvando(false)
    }
  }

  if (empresaSelfQuery.isLoading) {
    return <p>Carregando empresa...</p>
  }

  if (!empresa) {
    return (
      <div
        style={{
          padding: '1rem',
          background: 'var(--mantine-color-gray-0)',
          borderRadius: 8,
          fontSize: '0.875rem',
        }}
      >
        <strong>Empresa-self ainda não criada.</strong> O backend cria
        automaticamente quando o tenant é provisionado. Se essa mensagem
        persistir, fale com o suporte.
      </div>
    )
  }

  const corLogo = corDeNome(empresa.razao_social)
  const iniciais = iniciaisDe(empresa.razao_social)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <section>
        <h3 style={{ marginTop: 0 }}>Logo da sua empresa</h3>
        <p style={{ color: '#666', fontSize: '0.875rem', margin: '0 0 1rem' }}>
          A logo aparece nos PDFs gerados (PMOCs, OS, relatórios). Sem logo, é
          usada uma cor + as iniciais — como um avatar do GitHub.
        </p>

        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: 8,
              background: corLogo,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              fontWeight: 700,
              backgroundImage: 'logo' in empresa && (empresa as Empresa & { logo?: string }).logo
                ? `url(${(empresa as Empresa & { logo?: string }).logo})`
                : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            aria-label="Pré-visualização da logo"
            data-testid="empresa-logo-preview"
          >
            {!('logo' in empresa) || !(empresa as Empresa & { logo?: string }).logo
              ? iniciais
              : ''}
          </div>

          <div style={{ flex: 1 }}>
            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png,.svg,image/jpeg,image/png,image/svg+xml"
              data-testid="empresa-logo-input"
              onChange={(e) => selecionarLogo(e.target.files?.[0] ?? null)}
              disabled={!isAdmin}
            />
            <p style={{ color: '#666', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
              JPG, PNG ou SVG. Máximo 2MB. Recomendado 400x400 px (quadrado).
            </p>
            {logoErro && (
              <p style={{ color: '#C62828', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
                {logoErro}
              </p>
            )}
            {logoFile && !logoErro && (
              <button
                type="button"
                onClick={uploadLogo}
                disabled={salvando}
                style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#1565C0',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
                data-testid="empresa-logo-upload-btn"
              >
                {salvando ? 'Enviando...' : 'Enviar logo'}
              </button>
            )}
          </div>
        </div>
      </section>

      <section>
        <h3 style={{ marginTop: 0 }}>Papéis da sua empresa</h3>
        <p style={{ color: '#666', fontSize: '0.875rem', margin: '0 0 1rem' }}>
          Marque os papéis que sua empresa exerce em PMOCs. Pelo menos um é
          obrigatório.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PAPEIS_OPTIONS.map((p) => (
            <label
              key={p.value}
              title={p.tooltip}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                cursor: isAdmin ? 'pointer' : 'not-allowed',
                opacity: isAdmin ? 1 : 0.6,
              }}
            >
              <input
                type="checkbox"
                data-testid={`self-papel-${p.value.toLowerCase()}`}
                checked={papeisLocal.includes(p.value)}
                onChange={() => togglePapel(p.value)}
                disabled={!isAdmin}
              />
              <span>{p.label}</span>
            </label>
          ))}
        </div>

        {isAdmin && (
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={salvarPapeis}
              disabled={!podeSalvarPapeis || update.isPending}
              data-testid="self-papeis-salvar"
              style={{
                padding: '0.5rem 1rem',
                background: podeSalvarPapeis ? '#1565C0' : '#bdbdbd',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: podeSalvarPapeis ? 'pointer' : 'not-allowed',
                fontWeight: 600,
              }}
            >
              {update.isPending ? 'Salvando...' : 'Salvar papéis'}
            </button>
            {papeisLocal.length === 0 && (
              <p style={{ color: '#C62828', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
                Selecione pelo menos um papel.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
